import { Strings } from "src/modules/utils/shared/strings";
/*eslint-disable*/
import { Component, ElementRef, EventEmitter, forwardRef, inject, Input, NgZone, OnDestroy, OnInit, Output,
    ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as ace from "ace-builds";
import { Utils } from 'src/modules/utils/shared/utils';
import { AceSelection } from '../../models/ace-selection.model';
import { InFileError } from 'src/modules/enaio/netgen/InFileError';
import { DebuggingBreakpoint } from 'src/modules/enaio/netgen/DebuggingBreakpoint';
import { ErrorType } from 'src/modules/enaio/netgen/ErrorType';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { RestEndpoint } from "../../models/rest-endpoint.model";

@Component({
    selector: 'app-ace-editor',
    templateUrl: './ace-editor.component.html',
    styleUrls: ['./ace-editor.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AceEditorComponent),
            multi: true
        }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AceEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
    app = inject(MainAppService);
    el = inject(ElementRef);
    zone = inject(NgZone);

    @Input()
    style: any = {};
    @Input()
    allowBreakpoints = false;
    _breakpoints: DebuggingBreakpoint[] = null;
    _singleLine = false;
    _asTerminal = false;
    @Input()
    validateCallback: (language: string, text: string) => Promise<InFileError[]>;
    @Input()
    canSetBreakpoint: (line: number) => boolean;

    _options: any = {
        tabSize: 2,
        showPrintMargin: false
    };
    _readOnly: boolean = false;
    _theme: string = "github";
    _mode: any = "html";
    _autoUpdateContent: boolean = true;
    _editor: ace.Editor;
    _durationBeforeCallback: number = 0;
    _text: string = "";
    _lineBlockFoldingStartPattern: string = null;
    _collapseLineBlocks = false;
    oldText: any;
    timeoutSaving: any;
    validationTimer: any;
    validationRequest = 0;

    terminalHistory: string[] = [];
    terminalHistoryIndex = -1;
    highlights = [];
    dividers = [];

    @Output()
    textChange = new EventEmitter();
    @Output() 
    contextMenu = new EventEmitter<AceSelection>();
    @Output()
    clickToken = new EventEmitter<AceSelection>();
    @Output()
    breakpointAdded = new EventEmitter<number>();
    @Output()
    breakpointRemoved = new EventEmitter<number>();
    @Output()
    terminalCommand = new EventEmitter<string>();

    constructor() {
        //Muss hier oben stehen, da ein Aufruf in init zu spät ist
        ace.config.set('basePath', '/assets/ace');
        let el = this.el.nativeElement;
        this.zone.runOutsideAngular(() => {
            this._editor = ace['edit'](el);
        });
        //this._editor.$blockScrolling = Infinity;

        this.app.onDarkModeChanged.subscribe(() => this.setTheme(this._theme));
    }

    ngOnInit() {
        this.init();
        this.initEvents();
    }

    ngOnDestroy() {
        if (this.validationTimer) {
            clearTimeout(this.validationTimer);
        }
        this._editor.destroy();
    }

    @Input()
    set singleLine(value: boolean) {
        this._singleLine = value;
        if (this._singleLine) {
            this._editor.setOptions({
                maxLines: 1,
                minLines: 1,
                showGutter: false,
                highlightActiveLine: false,
                highlightGutterLine: false,
                printMargin: false,
                wrap: false
            });
        }
        else {
            //TODO
        }
    }

    @Input()
    set asTerminal(value: boolean) {
        this._asTerminal = value;
        if (this._asTerminal) {
            this._editor.commands.addCommand({
                name: "submit",
                bindKey: { win: "Enter", mac: "Enter" },
                exec: ed => {
                    const value = ed.getValue().trim();
                    if (!value) {
                        return;
                    }

                    this.terminalHistory.push(value);
                    this.terminalHistoryIndex = this.terminalHistory.length;

                    this.terminalCommand.emit(value);
                    ed.setValue("", -1);
                }
            });
            this._editor.commands.addCommand({
                name: "historyUp",
                bindKey: { win: "Up", mac: "Up" },
                exec: ed => {
                    if (this.terminalHistory.length === 0) {
                        return;
                    }

                    this.terminalHistoryIndex = Math.max(0, this.terminalHistoryIndex - 1);
                    ed.setValue(this.terminalHistory[this.terminalHistoryIndex], -1);
                }
            });
            this._editor.commands.addCommand({
                name: "historyDown",
                bindKey: { win: "Down", mac: "Down" },
                exec: ed => {
                    if (this.terminalHistory.length === 0) {
                        return;
                    }

                    this.terminalHistoryIndex = Math.min(this.terminalHistory.length, this.terminalHistoryIndex + 1);
                    if (this.terminalHistoryIndex === this.terminalHistory.length) {
                        ed.setValue("", -1);
                    }
                    else {
                        ed.setValue(this.terminalHistory[this.terminalHistoryIndex], -1);
                    }
                }
            });
            this._editor.session.on("change", () => {
                const value = this._editor.getValue();
                if (value.includes("\n")) {
                    this._editor.setValue(value.replace(/\r?\n/g, ""), -1);
                }
            });
        }
        else {
            //TODO
        }
    }

    @Input()
    set breakpoints(value: DebuggingBreakpoint[]) {
        this._breakpoints = value;
        this.updateEditorBreakpoints();
    }

    updateEditorBreakpoints() {
        let session = this._editor.getSession();
        session.clearBreakpoints();
        for (let bp of this._breakpoints ?? []) {
            this.setBreakpoint(bp.line - 1, true, false);
        }
    }

    lineHasBreakpoint(line: number): boolean {
        return this._editor.getSession().getBreakpoints()[line] != null;
    }

    setBreakpoint(line: number, enabled = true, emitEvent = true): void {
        let session = this._editor.getSession();
        if (enabled && !this.lineHasBreakpoint(line)) {
            if (this.canSetBreakpoint && !this.canSetBreakpoint(line)) {
                return;
            }
            session.setBreakpoint(line, undefined);
            if (emitEvent) {
                this.breakpointAdded.emit(line + 1);
            }
        }
        else if (!enabled && this.lineHasBreakpoint(line)) {
            session.clearBreakpoint(line);
            if (emitEvent) {
                this.breakpointRemoved.emit(line + 1);
            }
        }
    }

    init() {
        this.setOptions(this._options || {});
        this.setTheme(this._theme);
        this.setMode(this._mode);
        this.setReadOnly(this._readOnly);

        this._editor.container.addEventListener("click", e => {
            if (e.ctrlKey) {
                let pos = this._editor.renderer.screenToTextCoordinates(e.clientX, e.clientY);
                let token = this._editor.session.getTokenAt(pos.row, pos.column);
                let selection = this._editor.getSelectedText();

                if (token) {
                    this.clickToken.emit(new AceSelection(pos.row, pos.column, token.value, selection));
                }
            }
        });

        this._editor.container.addEventListener("contextmenu", e => {
            e.preventDefault();

            let pos = this._editor.renderer.screenToTextCoordinates(e.clientX, e.clientY);
            let token = this._editor.session.getTokenAt(pos.row, pos.column);
            let selection = this._editor.getSelectedText();

            this.contextMenu.emit(new AceSelection(pos.row, pos.column, token.value, selection));

            return false;
        });



        this._editor.on("gutterclick",e => {
            if (this.allowBreakpoints) {
            let target = e.domEvent.target;
            if (target.className.indexOf("ace_gutter-cell") == -1) {
                return;
            }
            let row = e.getDocumentPosition().row;
            this.setBreakpoint(row, !this.lineHasBreakpoint(e.getDocumentPosition().row));
            e.stop();
            }            
        });

       /* this._editor.on("mousemove", e => {
            let pos = e.getDocumentPosition();
            let token = this._editor.session.getTokenAt(pos.row, pos.column);
            console.log(token.type);
            if (token && token.type === "identifier") {
                this._editor.container.style.cursor = 'pointer';
            } else {
                this._editor.container.style.cursor = 'default';
            }
         });*/


        this._editor.container.addEventListener('mousemove',event => {
            const tooltip = document.querySelector('.ace_tooltip') as HTMLElement;
            if (tooltip != null) {
                const rect = tooltip.getBoundingClientRect();
                if (rect.top < 0) {
                    tooltip.style.maxHeight = rect.height + rect.top + "px";
                    tooltip.style.overflow = 'auto';
                    tooltip.style.whiteSpace = 'pre-wrap';
                }
            }     
        });
    }

    async validate(text = this._text): Promise<InFileError[]> {
        let validationText = Strings.getSubstringTo(
            text,
            "!!!!!!!!!!!!!!!!!!!! INKLUDIERTES SKRIPT"
        );
        if (this.validateCallback != null) {
            return this.validateCallback(this._mode, validationText);
        }
        if (this._mode == "json") {
            return RestEndpoint.main().post().body({ text: validationText }).run("api/app/validateJson").list(InFileError);
    }
        return [];
    }

    initEvents() {
        this._editor.getSession().on("changeMode", () => this.updateLineBlockFolding());
        this._editor.on('change', () => {
            this.updateText();
            this.scheduleValidation();
        });
        this._editor.on('paste', () => this.updateText());
    }

    scheduleValidation(): void {
        let request = ++this.validationRequest;
        if (this.validationTimer) {
            clearTimeout(this.validationTimer);
        }
        if (this._readOnly ||
            this.validateCallback == null && this._mode != "json") {
            return;
        }
        if (this._text == "") {
            this.setErrors([]);
            return;
        }
        this.validationTimer = Utils.setTimerOnce(400, async () => {
            let errors = await this.validate(this._text);
            if (request == this.validationRequest) {
                this.setErrors(errors);
            }
            this.validationTimer = null;
        });
    }

    updateText() {
        let newVal = this._editor.getValue();
        if (newVal === this.oldText) {
            return;
        }
        if (!this._durationBeforeCallback) {
            this._text = newVal;
            this.zone.run(() => {
                this.textChange.emit(newVal);
            });
            this._onChange(newVal);
        }
        else {
            if (this.timeoutSaving) {
                clearTimeout(this.timeoutSaving);
            }

            this.timeoutSaving = Utils.setTimerOnce(this._durationBeforeCallback, () => {
                this._text = newVal;
                this.zone.run(() => {
                    this.textChange.emit(newVal);
                });
                this.timeoutSaving = null;
            });
        }
        this.oldText = newVal;
    }

    @Input() set options(options: any) {
        this.setOptions(options);
    }

    setOptions(options: any) {
        this._options = {
            tabSize: 2,
            showPrintMargin: false,
            ...options || {}
        };
        this._editor.setOptions(this._options);
    }

    @Input() set readOnly(readOnly: any) {
        this.setReadOnly(readOnly);
    }

    setReadOnly(readOnly: any) {
        this._readOnly = readOnly;
        this._editor.setReadOnly(readOnly);
        this.updateValidationMode();
    }

    @Input() set theme(theme: any) {
        this.setTheme(theme);
    }

    setTheme(theme: any) {
        this._theme = theme;
        this._editor.setTheme(this.app.isDarkMode() ? "ace/theme/monokai" : "ace/theme/" + theme);
    }

    @Input() set mode(mode: any) {
        this.setMode(mode);
    }

    setMode(mode: any) {
        this._mode = mode;
        if (typeof this._mode === 'object') {
            this._editor.getSession().setMode(this._mode);
        }
        else {
            this._editor.getSession().setMode(`ace/mode/${this._mode}`);
        }
        this.updateLineBlockFolding();
        this.updateValidationMode();
    }

    @Input() set lineBlockFoldingStartPattern(pattern: string) {
        this._lineBlockFoldingStartPattern = pattern;
        this.updateLineBlockFolding();
    }

    @Input() set collapseLineBlocks(value: boolean) {
        this._collapseLineBlocks = value;
        this.collapseConfiguredLineBlocks();
    }

    private updateLineBlockFolding(): void {
        let session = this._editor.getSession() as any;
        if (this._lineBlockFoldingStartPattern == null || this._lineBlockFoldingStartPattern == "") {
            return;
        }

        let pattern = new RegExp(this._lineBlockFoldingStartPattern);
        let Range = ace.require("ace/range").Range;
        let findNextBlockStart = (row: number): number => {
            for (let nextRow = row + 1; nextRow < session.getLength(); nextRow++) {
                if (pattern.test(session.getLine(nextRow))) {
                    return nextRow;
                }
            }
            return session.getLength();
        };
        session.$setFolding({
            getFoldWidget: (_session: any, _foldStyle: string, row: number): string => {
                if (!pattern.test(session.getLine(row))) {
                    return "";
                }
                return findNextBlockStart(row) > row + 1 ? "start" : "";
            },
            getFoldWidgetRange: (_session: any, _foldStyle: string, row: number): any => {
                if (!pattern.test(session.getLine(row))) {
                    return null;
                }
                let endRow = findNextBlockStart(row) - 1;
                return endRow > row ? new Range(row, session.getLine(row).length, endRow, session.getLine(endRow).length) : null;
            }
        });
        this.collapseConfiguredLineBlocks();
    }

    private collapseConfiguredLineBlocks(): void {
        if (this._collapseLineBlocks && this._lineBlockFoldingStartPattern != null && this._lineBlockFoldingStartPattern != "") {
            let session = this._editor.getSession();
            session.unfold();
            session.foldAll();
        }
    }

    updateValidationMode(): void {
        this._editor.getSession().setUseWorker(this._mode != "json" && this.validateCallback == null);
        this.scheduleValidation();
    }

    get value() {
        return this.text;
    }

    @Input()
    set value(value: string) {
        this.setText(value);
    }

    writeValue(value: any) {
        this.setText(value);
    }

    private _onChange = (_: any) => {};

    registerOnChange(fn: any) {
        this._onChange = fn;
    }

    _onTouched = () => {};

    registerOnTouched(fn: any) {
        this._onTouched = fn;
    }

    get text() {
        return this._text;
    }

    @Input()
    set text(text: string) {
        this.setText(text);
        this.highlightLine(null);
    }

    setText(text: any) {
        if (text === null || text === undefined) {
            text = "";
        }
        if (this._text !== text && this._autoUpdateContent === true) {
            this._text = text;
            this._editor.setValue(text);
            this.collapseConfiguredLineBlocks();
            this._onChange(text);
            this._editor.clearSelection();
        }
    }

    @Input() set autoUpdateContent(status: any) {
        this.setAutoUpdateContent(status);
    }

    setAutoUpdateContent(status: any) {
        this._autoUpdateContent = status;
    }

    @Input() set durationBeforeCallback(num: number) {
        this.setDurationBeforeCallback(num);
    }

    setDurationBeforeCallback(num: number) {
        this._durationBeforeCallback = num;
    }

    getEditor() {
        return this._editor;
    }

    focus() {
        this._editor.focus();
    }

    jumpToLine(line: number, highlight = false, highlightStyleClass = "custom-highlight"): boolean {
        const session = this._editor?.getSession();
        if (session == null || !Number.isInteger(line) || line < 1 || line > session.getLength()) {
            return false;
        }

        const targetRow = line - 1;
        this._editor.resize(true);
        this._editor.gotoLine(line, 0, false);
        this._editor.scrollToLine(targetRow, true, false, () => {});

        const cursor = this._editor.getCursorPosition();
        const lineIsVisible = targetRow >= this._editor.getFirstVisibleRow() && targetRow <= this._editor.getLastVisibleRow();
        const success = cursor.row == targetRow && lineIsVisible;
        if (highlight && success) {
            this.highlightLine(line, false, highlightStyleClass);
        }
        return success;
    }

    highlightLine(lineNumber: number, scrollIntoView = false, styleClass = "custom-highlight") {
        let Range = ace.require("ace/range").Range;

        for (let highlight of this.highlights) {
            this._editor.session.removeMarker(highlight);
        }
        if (lineNumber == null) {
            return;
        }
        this.highlights.push(
            this._editor.session.addMarker(
                new Range(lineNumber - 1, 0, lineNumber - 1, Number.MAX_VALUE),
                styleClass,
                "fullLine",
            ),
        );

        if (scrollIntoView) {
            const targetRow = lineNumber - 1;

            const firstVisible = this._editor.getFirstVisibleRow();
            const lastVisible = this._editor.getLastVisibleRow();

            // Nur scrollen, wenn die Zeile nicht sichtbar ist
            if (targetRow < firstVisible) {
                // Zeile liegt oberhalb → 5 Zeilen vorher anzeigen
                this._editor.scrollToLine(
                    Math.max(0, targetRow - 5),
                    false,
                    true,
                    () => {},
                );
            }
            else if (targetRow > lastVisible) {
                // Zeile liegt unterhalb → 5 Zeilen danach anzeigen
                this._editor.scrollToLine(targetRow + 5, false, true, () => {});
            }
        }
    }

    addDivider(lineNumber: number) {
        var Range = ace.require("ace/range").Range;
        for (let divider of this.dividers) {
            this._editor.session.removeMarker(divider);
        }
        if (lineNumber != null) {
            this.dividers.push(
                this._editor.session.addMarker(
                    new Range(lineNumber, 0, lineNumber, 1),
                    "line-divider",
                    "fullLine",
                ),
            );
        }

        /*  var lineWidget = {
            row: lineNumber,
            fixedWidth: true,
            coverGutter: false,
            el: document.createElement("div"),
            type: "lineWidget"
        };

        lineWidget.el.className = 'line-divider';
        lineWidget.el.style.height = '2px';
        lineWidget.el.style.width = '100%';
        lineWidget.el.style.backgroundColor = '#000';

        this._editor.session.addDynamicMarker(lineWidget, true);*/
    }

    setErrors(errors: InFileError[]): void {
        let lineCount = Strings.splitLines(this._text).length;
        this._editor.session.setAnnotations(
            errors.map((error) => ({
                row: Math.min(Math.max(0, error.lineNumber - 1), lineCount - 1),
                column: error.columnNumber,
                type: error.errorType == ErrorType.error ? "error" : "warning",
                text: Strings.def(error.shortMessage, error.message),
            })),
        );
    }
}
